/**
 * Service Tracks Service
 *
 * Static service class for per-discipline service track API calls.
 * Follows the Component → Service → apiClient pattern — no direct fetch() calls.
 */
import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import type { ServiceTrackDto, Discipline } from '../../dtos/patientmr/certificationPeriod.dto';

export interface ActivateServiceTrackRequest {
  certificationPeriodId?: string;
  discipline: Discipline;
}

export class ServiceTracksService {
  /**
   * Activate a discipline service track (transition from SOC_IN_PROGRESS → ACTIVE).
   * All 8 SOC checklist items must be complete — backend enforces this.
   *
   * Returns 409 with { data: { incompleteItems: string[] } } when checklist
   * is incomplete. Callers should handle this to show a modal with blockers.
   */
  static async activate(
    patientId: string,
    payload: ActivateServiceTrackRequest
  ): Promise<ServiceTrackDto> {
    const response: ApiResponse<ServiceTrackDto> = await apiClient.post(
      `/patientmr/${patientId}/service-tracks/activate`,
      payload
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to activate service track');
  }

  /**
   * List all service tracks for a patient's certification period.
   */
  static async list(
    patientId: string,
    certificationPeriodId: string
  ): Promise<ServiceTrackDto[]> {
    const response: ApiResponse<ServiceTrackDto[]> = await apiClient.get(
      `/patientmr/${patientId}/service-tracks`,
      { certificationPeriodId }
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to list service tracks');
  }
}
